/**
 * Pure logic for the onboarding preview surface (Phase E1) — the authorization
 * decision table + tenant→house mapping, split out from PreviewFrame so it can
 * be unit-tested without a DOM.
 *
 * Security note: authorization is ultimately enforced by RLS (the tenant row
 * only resolves for owner/staff/super via `tenants_select_own_or_super`, and
 * the catalog only returns rows via the `*_tenant_staff_select` policies). This
 * table just maps the resolved facts to what the user sees — a null row (RLS
 * denied OR unknown slug) collapses to `denied`.
 */

export type PreviewStatus = 'loading' | 'unauthenticated' | 'denied' | 'ready';

/** The tenant columns the preview reads (all within the owner's RLS scope). */
export interface PreviewTenantRow {
  id: string;
  name: string;
  display_name: string | null;
  metadata: Record<string, unknown> | null;
}

/** Shape consumed by BidderHousesProvider's overrideHouse (a BidderHouse). */
export interface PreviewHouse {
  tenant_id: string;
  name: string;
  display_name: string | null;
  branding: Record<string, unknown> | null;
  tenant_bidder_number: null;
}

export interface PreviewStatusInput {
  /** Auth session still resolving. */
  sessionLoading: boolean;
  /** A signed-in session exists. */
  hasSession: boolean;
  /** The `:tenantSlug` route param (undefined if somehow absent). */
  tenantSlug: string | undefined;
  /** The tenant lookup has completed (resolved or errored). */
  queried: boolean;
  /** The tenant row RLS returned, or null (denied / unknown slug). */
  row: PreviewTenantRow | null;
  /** The tenant lookup returned a Postgres/transport error. */
  queryError: boolean;
}

/**
 * Map the resolved facts to the preview status. Order matters: auth is checked
 * before the tenant lookup so a signed-out visitor is asked to sign in rather
 * than seeing a bare "unavailable".
 */
export function resolvePreviewStatus(input: PreviewStatusInput): PreviewStatus {
  if (input.sessionLoading) return 'loading';
  if (!input.hasSession) return 'unauthenticated';
  if (!input.tenantSlug) return 'denied';
  if (!input.queried) return 'loading';
  if (input.queryError || !input.row) return 'denied';
  return 'ready';
}

/** Build the injected house from a resolved tenant row (branding from metadata). */
export function tenantRowToHouse(row: PreviewTenantRow): PreviewHouse {
  const meta = row.metadata ?? {};
  const branding = (meta.branding ?? null) as Record<string, unknown> | null;
  return {
    tenant_id: row.id,
    name: row.name,
    display_name: row.display_name,
    branding,
    tenant_bidder_number: null,
  };
}
